import { useEffect, useMemo, useState } from "react";
import {
  BriefcaseBusiness, Building2, Home, TrendingDown,
  AlertCircle, RefreshCw, ChevronDown,
} from "lucide-react";
import { FilterBar }   from "@/components/ui/FilterBar";
import { Card }        from "@/components/ui/Card";
import { Button }      from "@/components/ui/Button";
import { useModules }  from "@/context/ModulesContext";
import { formatCurrency, formatNumber, formatDate } from "@/utils/format";
import type { OutcomeItem } from "@/services/financeiro";

import {
  computeCustoUnitarioPorCC,
  computeCustoPorUnidadeConstrutiva,
  computeCustoAcumulado,
  computeCustoMensalPorCC,
  computeGerencialKpis,
  buildTotalInvestidoPlanoHierarchy,
  filterOutcomeByCostCenter,
} from "./chartUtils";

import { CustoUnitarioPorCC }       from "./charts/CustoUnitarioPorCC";
import { CustoPorUnidadeConstrutiva } from "./charts/CustoPorUnidadeConstrutiva";
import { CustoAcumuladoChart } from "./charts/CustoAcumuladoChart";
import { EvolucaoCustoMultiCC } from "./charts/EvolucaoCustoMultiCC";
import { TotalInvestidoPlanoModal } from "./TotalInvestidoPlanoModal";
import { DespesasDetailModal } from "@/pages/Financeiro/charts/DespesasDetailModal";
import { usePaymentCategories } from "@/hooks/usePaymentCategories";
import { useInstallmentsMap } from "@/hooks/useInstallmentsMap";

/* ─── Seletor de obra para detalhe ──────────────────────────────────────── */
interface ObraSelectorProps {
  options:   { value: string; label: string }[];
  value:     string;
  onChange:  (v: string) => void;
}

function ObraSelector({ options, value, onChange }: ObraSelectorProps) {
  return (
    <div className="relative inline-flex items-center gap-2">
      <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-2 text-sm text-slate-700 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 pointer-events-none" />
    </div>
  );
}

/* ─── Página ─────────────────────────────────────────────────────────────── */
export function GerencialInvestimentoPage() {
  const { gerencial, ccOptions, loadingCC, ccNameMap } = useModules();
  const { values, setValues, apply, reset, applied, data, loading, error } = gerencial;

  const paymentCatMap = usePaymentCategories();
  const installmentsMap = useInstallmentsMap();

  /* ── Modal: total investido por plano de contas ── */
  const [planoModalOpen, setPlanoModalOpen] = useState(false);
  type PlanoModalScope = "all" | { ccId: string };
  const [planoModalScope, setPlanoModalScope] = useState<PlanoModalScope>("all");
  const [parcelasModal, setParcelasModal] = useState<{
    title: string;
    items: OutcomeItem[];
  } | null>(null);

  /* ── Obra selecionada para detalhe de custo acumulado ── */
  const [selectedCCId, setSelectedCCId] = useState<string>("");

  /* ── Computações ── */
  const ccStats = useMemo(
    () => data
      ? computeCustoUnitarioPorCC(data.outcome, data.unitsByCC, ccNameMap)
      : [],
    [data, ccNameMap],
  );

  const ucStats = useMemo(
    () => (data?.outcome?.length ? computeCustoPorUnidadeConstrutiva(data.outcome) : []),
    [data?.outcome],
  );

  const hasBuildingUnitOnParcels = useMemo(
    () =>
      Boolean(
        data?.outcome?.some(
          (i) =>
            (i.buildingUnitId && String(i.buildingUnitId).trim() !== "") ||
            (i.buildingUnitName && String(i.buildingUnitName).trim() !== ""),
        ),
      ),
    [data?.outcome],
  );

  const avgTotalPorUC = useMemo(() => {
    if (ucStats.length === 0) return 0;
    const sum = ucStats.reduce((s, r) => s + r.totalCost, 0);
    return sum / ucStats.length;
  }, [ucStats]);

  const kpis        = useMemo(() => computeGerencialKpis(ccStats), [ccStats]);
  const isFirstLoad = loading && !data;
  /** Nenhuma consulta aplicada ainda (filtros só vão ao Sienge após Aplicar). */
  const awaitingCc =
    (!applied.costCenter?.length || applied.costCenter.length === 0) && data === null && !loading;
  const showIdleHint   = awaitingCc && !error;

  /* Opções de CC com custo para o seletor de detalhe */
  const ccDetailOptions = useMemo(
    () => ccStats.map((c) => ({ value: c.costCenterId, label: c.name })),
    [ccStats],
  );

  /* Auto-seleciona o primeiro CC quando os dados chegam */
  useEffect(() => {
    if (ccDetailOptions.length > 0 && !selectedCCId) {
      setSelectedCCId(ccDetailOptions[0].value);
    }
  }, [ccDetailOptions, selectedCCId]);

  /* Dados do detalhe para a obra selecionada */
  const selectedCC = useMemo(
    () => ccStats.find((c) => c.costCenterId === selectedCCId),
    [ccStats, selectedCCId],
  );

  const custoAcumulado = useMemo(
    () => selectedCC
      ? computeCustoAcumulado(selectedCC.items, selectedCC.unitCount)
      : [],
    [selectedCC],
  );

  /* Dados do gráfico multi-CC (custo unitário acumulado ao longo do tempo) */
  const multiCCData = useMemo(
    () => data
      ? computeCustoMensalPorCC(data.outcome, ccStats, data.unitsByCC)
      : [],
    [data, ccStats],
  );

  const planoModalSlice = useMemo(() => {
    if (!data?.outcome?.length) return [];
    return planoModalScope === "all"
      ? data.outcome
      : filterOutcomeByCostCenter(data.outcome, planoModalScope.ccId);
  }, [data?.outcome, planoModalScope]);

  const planoHierarchy = useMemo(
    () =>
      planoModalSlice.length > 0
        ? buildTotalInvestidoPlanoHierarchy(
            planoModalSlice,
            data?.categoryCodesById ?? new Map(),
            paymentCatMap,
          )
        : [],
    [planoModalSlice, data?.categoryCodesById, paymentCatMap],
  );

  const planoModalContextNote = useMemo(() => {
    if (planoModalScope === "all") return undefined;
    const row = ccStats.find(
      (c) => String(c.costCenterId).trim() === String(planoModalScope.ccId).trim(),
    );
    return row
      ? `Escopo: somente ${row.name}.`
      : `Escopo: somente centro de custo ${planoModalScope.ccId}.`;
  }, [planoModalScope, ccStats]);

  const grandInvestido = useMemo(
    () => planoModalSlice.reduce((s, i) => s + i.originalAmount, 0),
    [planoModalSlice],
  );

  const periodLabelModal =
    applied.startDate && applied.endDate
      ? `Período: ${formatDate(applied.startDate)} — ${formatDate(applied.endDate)}.`
      : "";

  const hasCharts = data !== null || loading;

  return (
    <>
    <div className="flex flex-col gap-6">

      {/* ── Cabeçalho ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <BriefcaseBusiness className="w-5 h-5 text-brand-600" />
            Gerencial
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Investimento por obra e por etapa (unidade construtiva), quando o Sienge envia esse vínculo nas parcelas
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={apply}
          loading={loading}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          Atualizar
        </Button>
      </div>

      {/* ── Filtros ── */}
      <FilterBar
        values={values}
        onChange={setValues}
        onApply={apply}
        onReset={reset}
        costCenterOptions={ccOptions}
        loadingOptions={loadingCC}
        loading={loading}
      />

      {/* ── Erro ── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={apply}
            className="ml-auto text-red-500 hover:text-red-700 underline text-xs"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* ── Estado inicial: nenhuma consulta ainda ── */}
      {showIdleHint && (
        <div className="flex items-start gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
          <span>
            Selecione um ou mais <strong>centros de custo</strong> e o <strong>período</strong>, depois clique em{" "}
            <strong>Aplicar</strong> na barra de filtros para carregar os dados do Sienge. Use <strong>Atualizar</strong>{" "}
            para repetir a consulta com os mesmos filtros aplicados.
          </span>
        </div>
      )}

      {/* ── KPI Cards ── */}
      {(data !== null || loading) && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card
          title="Total Investido"
          value={isFirstLoad ? undefined : formatCurrency(kpis.totalCost)}
          subtitle={isFirstLoad ? undefined : `${formatNumber(kpis.ccCount)} obra${kpis.ccCount !== 1 ? "s" : ""} · planos 210–211–207–201–202 · clique para detalhar`}
          icon={<TrendingDown className="w-5 h-5" />}
          loading={isFirstLoad}
          className="border-l-4 border-l-rose-400"
          onClick={
            !isFirstLoad && data && data.outcome.length > 0
              ? () => {
                  setPlanoModalScope("all");
                  setPlanoModalOpen(true);
                }
              : undefined
          }
        />
        <Card
          title="Total de Unidades"
          value={isFirstLoad ? undefined : formatNumber(kpis.totalUnits)}
          subtitle={
            isFirstLoad
              ? undefined
              : kpis.totalUnits === 0
                ? "Aguardando dados de unidades"
                : `casas mapeadas`
          }
          icon={<Home className="w-5 h-5" />}
          loading={isFirstLoad}
          className="border-l-4 border-l-blue-400"
        />
        <Card
          title="Custo Unitário Médio"
          value={
            isFirstLoad
              ? undefined
              : kpis.totalUnits > 0
                ? formatCurrency(kpis.avgUnitCost)
                : "—"
          }
          subtitle={
            isFirstLoad
              ? undefined
              : kpis.totalUnits > 0
                ? "R$ por unidade construtiva"
                : "Sem unidades mapeadas"
          }
          icon={<Building2 className="w-5 h-5" />}
          loading={isFirstLoad}
          className="border-l-4 border-l-violet-400"
        />
      </div>
      )}

      {(isFirstLoad || (loadingCC && data === null && !loading)) && (
        <div className="text-center py-4">
          <p className="text-sm text-slate-500">
            {loadingCC && !data
              ? "Carregando centros de custo…"
              : "Consultando o Sienge… isso pode levar alguns segundos."}
          </p>
        </div>
      )}

      {/* ══════════════════ SEÇÃO: CUSTO POR OBRA OU POR U.C. ══════════════════ */}
      {hasCharts && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              {hasBuildingUnitOnParcels
                ? "Custo por unidade construtiva"
                : "Custo unitário por obra"}
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Parcelas sem vínculo de U.C.: mostramos gráfico por CC e aviso */}
          {!isFirstLoad && data && data.outcome.length > 0 && !hasBuildingUnitOnParcels && (
            <div className="flex items-start gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
              <span>
                As parcelas do período não trazem <strong>unidade construtiva</strong> no bulk do Sienge.
                Exibindo <strong>custo unitário por centro de custo</strong> (R$/casa). Quando o vínculo existir nas parcelas, o gráfico passa a agrupar por etapa, como no Orçamento.
              </span>
            </div>
          )}

          {/* Aviso quando não há unidades mapeadas (só relevante no modo por CC) */}
          {!isFirstLoad &&
            data &&
            data.unitsByCC.length === 0 &&
            !hasBuildingUnitOnParcels && (
            <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Nenhuma unidade (casa) foi encontrada na API. O custo unitário será zero.
                Verifique as permissões de acesso ao endpoint de unidades no Sienge.
              </span>
            </div>
          )}

          {hasBuildingUnitOnParcels ? (
            <CustoPorUnidadeConstrutiva
              data={ucStats}
              loading={isFirstLoad}
              avgTotal={avgTotalPorUC}
            />
          ) : (
            <CustoUnitarioPorCC
              data={ccStats}
              loading={isFirstLoad}
              avgCost={kpis.avgUnitCost}
              periodLabel={periodLabelModal}
              startDate={applied.startDate}
              endDate={applied.endDate}
              onOpenInvestidoPorPlano={(row) => {
                setPlanoModalScope({ ccId: row.costCenterId });
                setPlanoModalOpen(true);
              }}
            />
          )}

          {/* ══════════════════ SEÇÃO: EVOLUÇÃO ACUMULADA ══════════════════ */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Evolução do Custo Acumulado
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Gráfico multi-CC: custo unitário acumulado por obra */}
          <EvolucaoCustoMultiCC
            data={multiCCData}
            ccStats={ccStats}
            loading={isFirstLoad}
          />

          {/* ══════════════════ SEÇÃO: DETALHE DA OBRA ══════════════════ */}
          {ccStats.length > 0 && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
                  Detalhe por Obra
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Seletor de obra + cards de detalhe */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-card px-5 py-4 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm font-semibold text-slate-700">Obra selecionada</span>
                  {ccDetailOptions.length > 0 && (
                    <ObraSelector
                      options={ccDetailOptions}
                      value={selectedCCId}
                      onChange={setSelectedCCId}
                    />
                  )}
                </div>

                {selectedCC && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-rose-50 rounded-xl p-4 text-center">
                      <p className="text-xs font-semibold text-rose-600 uppercase tracking-wide">
                        Custo Total
                      </p>
                      <p className="text-xl font-bold text-slate-800 mt-1">
                        {formatCurrency(selectedCC.totalCost)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                        Unidades
                      </p>
                      <p className="text-xl font-bold text-slate-800 mt-1">
                        {formatNumber(selectedCC.unitCount)} casas
                      </p>
                    </div>
                    <div className="bg-violet-50 rounded-xl p-4 text-center">
                      <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide">
                        Custo Unitário
                      </p>
                      <p className="text-xl font-bold text-slate-800 mt-1">
                        {selectedCC.unitCount > 0
                          ? formatCurrency(selectedCC.custoUnitario)
                          : "—"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">por casa</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Gráfico de custo acumulado da obra selecionada */}
              <CustoAcumuladoChart
                data={custoAcumulado}
                loading={isFirstLoad}
                unitCount={selectedCC?.unitCount ?? 0}
                ccName={selectedCC?.name}
              />
            </>
          )}
        </>
      )}
    </div>

      <TotalInvestidoPlanoModal
        open={planoModalOpen}
        onClose={() => {
          setPlanoModalOpen(false);
          setPlanoModalScope("all");
        }}
        hierarchy={planoHierarchy}
        grandTotal={grandInvestido}
        periodLabel={periodLabelModal}
        contextNote={planoModalContextNote}
        onOpenParcelas={(items, title) => {
          setParcelasModal({
            title: `Pagamentos — ${title}`,
            items,
          });
        }}
      />

      <DespesasDetailModal
        open={parcelasModal != null}
        onClose={() => setParcelasModal(null)}
        title={parcelasModal?.title ?? ""}
        items={parcelasModal?.items ?? []}
        installmentsMap={installmentsMap}
        ccNameMap={ccNameMap}
        paymentCatMap={paymentCatMap}
      />
    </>
  );
}
